import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import api, { eur } from "@/lib/api";
import { Printer, ArrowLeft, Mail, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { sendViaEmail, sendViaWhatsApp, uploadDocument, buildWhatsAppUrl } from "@/lib/pdfSend";
import { docTexts } from "@/lib/docTexts";

const PAYMENT = { conto_corrente: "Conto corrente", cash: "Cash" };

export default function Ricevuta() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const autoDone = useRef(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);
  const [acconto, setAcconto] = useState("");

  useEffect(() => {
    api.get(`/bookings/${id}/full`).then((r) => setData(r.data)).catch(() => setError("Prenotazione non trovata"));
  }, [id]);

  useEffect(() => {
    if (autoDone.current || !data) return;
    const s = searchParams.get("send");
    if (s !== "email" && s !== "whatsapp") return;
    autoDone.current = true;
    const bk = data.booking;
    (async () => {
      setSending(true);
      try {
        await new Promise((r) => setTimeout(r, 400));
        const el = document.querySelector('[data-testid="ricevuta-sheet"]');
        if (!el) { setSending(false); return; }
        if (s === "email") {
          const email = bk.guest_email || window.prompt("Email destinatario:") || "";
          if (email) { await sendViaEmail(bk.id, "ricevuta", el, email); toast.success(`Ricevuta inviata a ${email}`); }
        } else {
          const phone = bk.guest_phone || window.prompt("Numero WhatsApp (con prefisso, es. +39...):") || "";
          if (phone) {
            const d = await uploadDocument(bk.id, "ricevuta", el);
            toast.success("Apertura WhatsApp…");
            window.location.href = buildWhatsAppUrl(d.public_url, "ricevuta", phone, `${bk.guest_name} ${bk.guest_surname}`);
          }
        }
      } catch (e) { toast.error("Invio non riuscito"); }
      setSending(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, searchParams]);

  if (error) return <div className="p-10 text-center text-slate-500">{error}</div>;
  if (!data) return <div className="p-10 text-center text-slate-400">Caricamento…</div>;

  const { booking: b, structure, room, nights, settings } = data;
  const t = docTexts(settings);
  const primary = (b.guests && b.guests[0]) || { name: b.guest_name, surname: b.guest_surname };
  const today = new Date().toLocaleDateString("it-IT");
  const inst = !structure.esterna && b.installments_enabled && (b.installments || []).length > 0;
  const fmtDate = (d) => { if (!d) return ""; const x = new Date((d || "").slice(0, 10)); return isNaN(x) ? d : x.toLocaleDateString("it-IT"); };

  const doSend = async (channel) => {
    if (sending) return;
    const el = document.querySelector('[data-testid="ricevuta-sheet"]');
    if (!el) return;
    setSending(true);
    try {
      if (channel === "email") {
        let email = b.guest_email;
        if (!email) email = window.prompt("Email destinatario:") || "";
        if (!email) { setSending(false); return; }
        await sendViaEmail(b.id, "ricevuta", el, email);
        toast.success(`Ricevuta inviata a ${email}`);
      } else {
        let phone = b.guest_phone;
        if (!phone) phone = window.prompt("Numero WhatsApp (con prefisso, es. +39...):") || "";
        if (!phone) { setSending(false); return; }
        await sendViaWhatsApp(b.id, "ricevuta", el, phone, `${b.guest_name} ${b.guest_surname}`);
        toast.success("Apertura WhatsApp…");
      }
    } catch (e) {
      toast.error("Invio non riuscito");
    }
    setSending(false);
  };

  return (
    <div className="min-h-screen bg-slate-100 py-8 px-4">
      <style>{`@media print { .no-print { display: none !important; } body { background: white; } .sheet { box-shadow: none !important; } }`}</style>

      <div className="max-w-2xl mx-auto mb-4 flex justify-between no-print">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900">
          <ArrowLeft size={16} /> Indietro
        </button>
        <div className="flex gap-2">
          <button onClick={() => doSend("email")} disabled={sending} data-testid="send-ricevuta-email" className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold px-3 py-2 rounded-md disabled:opacity-50">
            <Mail size={16} /> Email
          </button>
          <button onClick={() => doSend("whatsapp")} disabled={sending} data-testid="send-ricevuta-whatsapp" className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold px-3 py-2 rounded-md disabled:opacity-50">
            <MessageCircle size={16} /> WhatsApp
          </button>
          {structure.esterna && (
            <div className="flex items-center gap-1.5 bg-white border border-slate-300 rounded-md px-2" data-testid="ricevuta-acconto-wrap">
              <span className="text-xs text-slate-500">Acconto €</span>
              <input data-testid="ricevuta-acconto-input" type="number" min="0" value={acconto} onChange={(e) => setAcconto(e.target.value)} className="w-24 text-sm outline-none py-2" placeholder="0" />
            </div>
          )}
          <button onClick={() => window.print()} data-testid="print-ricevuta" className="flex items-center gap-2 bg-sky-700 hover:bg-sky-800 text-white text-sm font-semibold px-4 py-2 rounded-md">
            <Printer size={16} /> Stampa
          </button>
        </div>
      </div>

      <div className="sheet max-w-2xl mx-auto bg-white p-10 shadow-sm" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }} data-testid="ricevuta-sheet">
        <div className="flex justify-between items-start border-b-2 border-slate-800 pb-3">
          <div className="flex items-center gap-4">
            {!structure.esterna && <img src="/blu-lodge-logo.png" alt="Blu Lodge Maldives" className="w-24 h-24 object-contain shrink-0" data-testid="ricevuta-logo" />}
            <div>
              <h1 className="text-2xl font-extrabold text-slate-900 tracking-tight" style={{ fontFamily: "'Manrope', sans-serif" }}>
                {(structure.name || "STRUTTURA").toUpperCase()}
              </h1>
              <div className="text-xs text-slate-500">Ricevuta / Receipt{structure.tin ? ` · TIN ${structure.tin}` : ""}</div>
            </div>
          </div>
          <div className="text-right text-xs text-slate-500">
            <div>Data: {today}</div>
            <div>Rif. #{(b.id || "").slice(0, 8).toUpperCase()}</div>
          </div>
        </div>

        <div className="py-6">
          <div className="text-xs uppercase tracking-wide text-slate-400">Intestatario</div>
          <div className="text-2xl font-extrabold text-slate-900" style={{ fontFamily: "'Manrope', sans-serif" }}>
            {`${primary.name || ""} ${primary.surname || ""}`.trim() || "—"}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 text-sm border-y border-slate-200 py-4">
          <Row label="Struttura" value={`${structure.name || ""}`} />
          <Row label="Camera" value={`${room.name || ""}${room.type ? ` · ${room.type}` : ""}`} />
          <Row label="Check-in" value={`${b.checkin} ${b.checkin_time || ""}`} />
          <Row label="Check-out" value={`${b.checkout} ${b.checkout_time || ""}`} />
          <Row label="Notti" value={nights} />
          <Row label="Ospiti" value={`${(b.adults || 0)} adulti · ${(b.children || 0)} bambini`} />
        </div>

        {!structure.esterna && !inst && (<>
        <table className="w-full text-sm mt-6">
          <tbody>
            <tr className="border-b border-slate-100">
              <td className="py-2 text-slate-600">Soggiorno ({nights} notti)</td>
              <td className="py-2 text-right tabular font-medium">{eur(b.total_amount)}</td>
            </tr>
            <tr className="border-b border-slate-100">
              <td className="py-2 text-slate-600">Acconto versato</td>
              <td className="py-2 text-right tabular text-emerald-600">− {eur(b.acconto)}</td>
            </tr>
            <tr className="border-b border-slate-200">
              <td className="py-2 text-slate-600">Saldo</td>
              <td className="py-2 text-right tabular text-amber-600">{eur(b.saldo)}</td>
            </tr>
            <tr>
              <td className="py-3 text-lg font-bold text-slate-900" style={{ fontFamily: "'Manrope', sans-serif" }}>Totale</td>
              <td className="py-3 text-right text-lg font-extrabold tabular text-slate-900">{eur(b.total_amount)}</td>
            </tr>
          </tbody>
        </table>

        <div className="mt-6" data-testid="ricevuta-payment-status">
          {b.payment_status === "full" ? (
            <div className="rounded-md border-2 border-emerald-300 bg-emerald-50 px-4 py-3 text-center">
              <div className="text-lg font-extrabold text-emerald-700 tracking-wide" style={{ fontFamily: "'Manrope', sans-serif" }}>SALDO EFFETTUATO</div>
              <div className="text-xs text-emerald-600 mt-1">Pagamento completato · nessun importo residuo</div>
            </div>
          ) : b.payment_status === "partial" ? (
            <div className="rounded-md border-2 border-amber-300 bg-amber-50 px-4 py-3 text-center">
              <div className="text-lg font-extrabold text-amber-700 tracking-wide" style={{ fontFamily: "'Manrope', sans-serif" }}>ACCONTO VERSATO</div>
              <div className="text-sm text-amber-700 mt-1">Ricevuta emessa per il <b>solo acconto</b>. Saldo ancora da corrispondere: <b>{eur(b.saldo)}</b></div>
            </div>
          ) : (
            <div className="rounded-md border-2 border-orange-300 bg-orange-50 px-4 py-3 text-center">
              <div className="text-lg font-extrabold text-orange-700 tracking-wide" style={{ fontFamily: "'Manrope', sans-serif" }}>PAGAMENTO NON ANCORA RICEVUTO</div>
              <div className="text-sm text-orange-700 mt-1">Importo da corrispondere: <b>{eur(b.total_amount)}</b></div>
            </div>
          )}
        </div>

        <div className="text-sm text-slate-500 mt-4">Metodo acconto: <b className="text-slate-700">{PAYMENT[b.acconto_method] || b.acconto_method || "—"}</b> · Metodo saldo: <b className="text-slate-700">{PAYMENT[b.saldo_method] || b.saldo_method || "—"}</b></div>
        {(b.acconto_due_date || b.saldo_due_date) && (
          <div className="mt-4 rounded-md bg-slate-50 border border-slate-200 p-4 text-sm">
            <div className="font-semibold text-slate-700 mb-2" style={{ fontFamily: "'Manrope', sans-serif" }}>Termini di pagamento</div>
            <div className="flex justify-between border-b border-slate-100 py-1">
              <span className="text-slate-500">Acconto 30% {b.acconto_due_date ? `entro il ${b.acconto_due_date}` : ""}</span>
              <span className="tabular font-medium">{eur(b.acconto)}</span>
            </div>
            <div className="flex justify-between py-1">
              <span className="text-slate-500">Saldo 70% {b.saldo_due_date ? `entro il ${b.saldo_due_date}` : ""}</span>
              <span className="tabular font-medium">{eur(b.saldo)}</span>
            </div>
          </div>
        )}
        </>)}
        {inst && (<>
        <table className="w-full text-sm mt-6">
          <tbody>
            <tr className="border-b border-slate-100">
              <td className="py-2 text-slate-600">Soggiorno ({nights} notti)</td>
              <td className="py-2 text-right tabular font-medium">{eur(b.total_amount)}</td>
            </tr>
            <tr>
              <td className="py-3 text-lg font-bold text-slate-900" style={{ fontFamily: "'Manrope', sans-serif" }}>Totale</td>
              <td className="py-3 text-right text-lg font-extrabold tabular text-slate-900">{eur(b.total_amount)}</td>
            </tr>
          </tbody>
        </table>
        <div className="mt-6 rounded-md bg-slate-50 border border-slate-200 p-4 text-sm" data-testid="ricevuta-installments">
          <div className="font-semibold text-slate-700 mb-2" style={{ fontFamily: "'Manrope', sans-serif" }}>Piano di pagamento — acconto + 3 rate</div>
          {(b.installments || []).map((r, i) => (
            <div key={i} className="flex justify-between items-center border-b border-slate-100 last:border-0 py-1.5">
              <span className="text-slate-600">
                {i === 0 ? "Acconto" : `Rata ${i}`}{r.due_date ? ` — entro il ${fmtDate(r.due_date)}` : ""}
                {r.paid
                  ? <span className="ml-2 text-xs font-semibold text-emerald-700">· Incassata</span>
                  : <span className="ml-2 text-xs font-semibold text-amber-600">· Da versare</span>}
              </span>
              <span className="tabular font-medium">{eur(r.amount)}</span>
            </div>
          ))}
        </div>
        </>)}
        {structure.esterna && (
          <div className="mt-8 rounded-md border-2 border-emerald-300 bg-emerald-50 px-4 py-5 text-center" data-testid="ricevuta-acconto-esterna">
            <div className="text-xs uppercase tracking-wide text-emerald-600">Acconto ricevuto / Deposit received</div>
            <div className="text-3xl font-extrabold text-emerald-700 tabular mt-1" style={{ fontFamily: "'Manrope', sans-serif" }}>{eur(Number(acconto) || 0)}</div>
          </div>
        )}
        {!structure.esterna && (<>
        <div className="mt-8 pt-4 border-t border-slate-200 text-xs text-slate-500 leading-relaxed">
          {t.policyText}{" "}
          <a href={t.policyUrl} target="_blank" rel="noopener noreferrer" className="text-sky-700 underline break-all">
            {t.policyUrl}
          </a>
        </div>
        <div className="mt-3 text-sm font-semibold text-slate-700 leading-relaxed">
          {t.nb}
        </div>
        </>)}
        <div className="text-xs text-slate-400 mt-4">Grazie per aver scelto {structure.name || "la nostra struttura"}.</div>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-slate-400">{label}</div>
      <div className="font-semibold text-slate-800">{value}</div>
    </div>
  );
}
